import React, { useState, useEffect } from "react";
import { Tab, Card } from "@kelp-dao/kernel-design-system";
import { GoArrowUpRight } from "react-icons/go";
import { useRouter } from "next/router";
import { useUtmLink } from "@hooks/useUtm";
import Deposit from "./Deposit";
import Withdraw from "./Withdraw";
import Claim from "./Claim";
import { trackEvent } from "utils";
import { VAULT_DWB_TAB_CHANGE } from "@constants/analytics";

/**
 * Simplified Vault DWB (Deposit/Withdraw/Bridge) Component
 *
 * This component provides a tabbed interface for vault operations.
 * To onboard a new vault:
 * 1. Create hooks that return the required props for each operation
 * 2. Pass the hook results to this component
 * 3. Configure which tabs to show
 *
 * Example usage:
 * ```
 * const depositProps = useVaultDeposit();
 * const withdrawProps = useVaultWithdraw();
 *
 * <VaultDWB
 *   availableTabs={['deposit', 'withdraw', 'bridge']}
 *   depositProps={depositProps}
 *   withdrawProps={withdrawProps}
 *   bridgeRedirectUrl="/bridge-wrap-ageth"
 * />
 * ```
 */
interface VaultDWBProps {
  // Tab configuration
  availableTabs: Array<"deposit" | "withdraw" | "bridge" | "claim">;
  tabLabels?: {
    deposit?: string;
    withdraw?: string;
    bridge?: string;
    claim?: string;
  };
  initialTab?: "deposit" | "withdraw" | "bridge" | "claim";

  // Component props (only needed if tab is enabled)
  depositProps?: React.ComponentProps<typeof Deposit>;
  withdrawProps?: React.ComponentProps<typeof Withdraw>;
  claimProps?: React.ComponentProps<typeof Claim>;

  // Bridge redirect URL (opens in new tab when bridge tab is clicked)
  bridgeRedirectUrl?: string;

  // Optional custom claim component (overrides default Claim)
  customClaimComponent?: React.ComponentType<
    React.ComponentProps<typeof Claim>
  >;
}

const VaultDWB = ({
  availableTabs,
  tabLabels = {},
  initialTab,
  depositProps,
  withdrawProps,
  claimProps,
  bridgeRedirectUrl = "/bridge-wrap-ageth",
  customClaimComponent,
}: VaultDWBProps) => {
  const router = useRouter();
  const { href: finalBridgeHref } = useUtmLink(bridgeRedirectUrl);
  const [activeTab, setActiveTab] = useState<string>(
    initialTab || availableTabs[0]
  );

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    trackEvent(VAULT_DWB_TAB_CHANGE, {
      vault_tab: tab,
      vault_uri: router.pathname,
    });
  };

  // Handle bridge tab redirect
  useEffect(() => {
    if (activeTab === "bridge" && finalBridgeHref) {
      // Construct full URL including basePath for preview deployments
      const origin =
        typeof window !== "undefined" ? window.location.origin : "";
      const basePath = router.basePath || "";
      const fullUrl = `${origin}${basePath}${finalBridgeHref}`;

      window.open(fullUrl, "_blank");
      // Reset to previous tab (deposit by default)
      setActiveTab(initialTab || "deposit");
    }
  }, [activeTab, finalBridgeHref, initialTab, router.basePath]);

  const getTabLabel = (tabKey: string) => {
    const customLabel = tabLabels[tabKey as keyof typeof tabLabels];
    const defaultLabels = {
      deposit: "Deposit",
      withdraw: "Withdraw",
      bridge: "Bridge",
      claim: "Claim",
    };

    const labelText =
      customLabel ||
      defaultLabels[tabKey as keyof typeof defaultLabels] ||
      tabKey;

    // Add icon to bridge tab to indicate external link
    if (tabKey === "bridge") {
      return (
        <span className="flex items-center gap-1">
          {labelText}
          <GoArrowUpRight className="ml-0.5" fontSize="14px" />
        </span>
      );
    }

    return labelText;
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "deposit":
        return depositProps ? <Deposit {...depositProps} /> : null;

      case "withdraw":
        return withdrawProps ? <Withdraw {...withdrawProps} /> : null;

      case "bridge":
        // Bridge tab redirects, no content needed
        return null;

      case "claim":
        if (customClaimComponent && claimProps) {
          const CustomClaimComponent = customClaimComponent;
          return <CustomClaimComponent {...claimProps} />;
        }
        return claimProps ? <Claim {...claimProps} /> : null;

      default:
        return null;
    }
  };

  // Create tabs data for Kernel Design System Tab component
  const tabsData = availableTabs.map((tabKey) => ({
    label: getTabLabel(tabKey) || tabKey,
    value: tabKey,
  })) as any;

  return (
    <div className="flex flex-col gap-4 w-full">
      <Card
        className="p-4 md:p-6 rounded-xl bg-white border border-border-default hover:border-[#B6DFDF] transition-all duration-200"
        variant="non-bordered"
        style={{ overflow: "visible" }}
      >
        <div>
          {/* Tabs */}
          <div className="mb-0">
            <Tab
              tabs={tabsData}
              activeTab={activeTab}
              onTabChange={handleTabChange}
              innerContainerClassName="gap-1"
            />
          </div>

          {/* Tab Content */}
          {renderTabContent()}
        </div>
      </Card>
    </div>
  );
};

export default VaultDWB;
